import React, { useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService, toFlagId, type FlagId } from '@atlassian/jira-flags';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useRestoreFields } from '@atlassian/jira-servicedesk-custom-fields/src/services/use-restore-fields/index.tsx';
import { RESPONDERS_FIELD_KEY } from '../../../common/constants';
import { NewField } from '../../../common/ui/new-field';
import messages from './messages';

const RESPONDERS_WARNING_FLAG = 'create-responders-field-warning-flag';

type Props = {
	isFieldMissing: boolean;
	isFlagVisible: boolean;
	setFlagVisible: (arg1: boolean) => void;
};

const CreateRespondersFieldWarningFlag = ({ warningFlagId }: { warningFlagId: FlagId }) => {
	const { showFlag, dismissFlag } = useFlagsService();
	const { formatMessage } = useIntl();
	const [, { restoreFields }] = useRestoreFields(RESPONDERS_FIELD_KEY);

	return (
		<WarningFlag
			id={RESPONDERS_WARNING_FLAG}
			appearance="warning"
			title={formatMessage(messages.createRespondersFieldFlagTitle)}
			description={formatMessage(messages.createRespondersFieldFlagDescription)}
			actions={[
				{
					content: formatMessage(messages.createRespondersFieldFlagButton),
					onClick: (_, createButtonAnalyticsEvent: UIAnalyticsEvent) => {
						fireUIAnalytics(createButtonAnalyticsEvent, 'missingRespondersFieldWarningFlag');
						restoreFields(RESPONDERS_FIELD_KEY)
							.then(() => {
								showFlag({
									type: 'success',
									title: messages.createRespondersFieldSuccessFlagTitle,
									description: messages.createRespondersFieldSuccessFlagDescription,
									actions: [
										{
											onClick: () => {
												window.open(
													'https://support.atlassian.com/jira-service-management-cloud/docs/add-or-remove-the-responders-panel-from-your-service-project/',
													'_blank',
												);
											},
											content: messages.createRespondersFieldSuccessAlertEnabledLearnMoreLink,
										},
									],
								});
								fireOperationalAnalytics(
									createButtonAnalyticsEvent,
									'missingRespondersFieldCreated success',
								);
							})
							.catch((error) => {
								showFlag({
									type: 'error',
									title: messages.createRespondersFieldErrorFlagTitle,
									description: messages.createRespondersFieldErrorFlagDescription,
								});
								fireErrorAnalytics({
									event: createButtonAnalyticsEvent,
									meta: {
										id: 'missingRespondersFieldCreated',
										packageName: 'jiraServicedeskIncidentManagementSettings',
									},
									error,
									sendToPrivacyUnsafeSplunk: true,
								});
							});
						dismissFlag(warningFlagId);
					},
				},
			]}
		/>
	);
};

export const RespondersField = ({ isFieldMissing, isFlagVisible, setFlagVisible }: Props) => {
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const flagViewEvent = createAnalyticsEvent({});
	const warningFlagId = toFlagId(RESPONDERS_WARNING_FLAG);

	useEffect(() => {
		if (isFieldMissing && !isFlagVisible) {
			setFlagVisible(true);
			showFlag({
				id: warningFlagId,
				render: () => <CreateRespondersFieldWarningFlag warningFlagId={warningFlagId} />,
			});
			fireUIAnalytics(flagViewEvent, 'flag viewed', 'createRespondersWarningFlag');
		}
	}, [flagViewEvent, isFieldMissing, isFlagVisible, setFlagVisible, showFlag, warningFlagId]);

	return (
		<NewField
			icon={<PeopleGroupIcon label="" />}
			title={formatMessage(messages.respondersSubTitleHeading)}
			description={formatMessage(messages.respondersSubTitleBody)}
			link={{
				href: 'https://support.atlassian.com/jira-service-management-cloud/docs/add-or-remove-the-responders-panel-from-your-service-project/',
				text: formatMessage(messages.respondersSubTitleLearnMoreLink),
				inProductHelpArticleId: '75mWfCRSrk5DEh7TCAmLLP',
			}}
			isFieldMissing={isFieldMissing}
		/>
	);
};
